import { useMemo, useState } from "react";

// Small reusable table-sort hook (#557). Rows are sorted by a column key whose
// value is produced by an accessor, so a "column" can be a derived string (e.g.
// a person's full name) rather than a raw field. Click a header to sort by it;
// click again to flip asc/desc.
//
//   const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(rows, {
//     name:  (r) => fullName(r),
//     usefId:(r) => r.usefId,
//   }, "name");
//
// Comparison is type-aware: numbers compare numerically, everything else via a
// case-insensitive localeCompare. Empty / null values sort last regardless of
// direction so blank cells never jump to the top.
export function useSortedRows(rows, accessors, initialKey) {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    const accessor = accessors[sortKey];
    if (!accessor) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const isEmpty = (v) => v === null || v === undefined || v === "";
    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // Blanks always sort last, independent of direction.
      if (isEmpty(av) && isEmpty(bv)) return 0;
      if (isEmpty(av)) return 1;
      if (isEmpty(bv)) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
    });
    // accessors is a stable literal per render but its identity changes; key/dir
    // + rows are what actually drive a re-sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

export default useSortedRows;
